import { ProjectInfo, UserParameter } from '../models/project-info';
import { heatInputStages, HeatInputStageResolved } from '../services/heat-input-stages';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { ProjectService } from '../services/project.service';
import { ProjectTemperatureData } from '../models/project-temperature-data';
import { CureSheetService } from '../services/cure-sheet.service';
import {
  CureSheetProvenance,
  CureSheetRef,
  describeCureSheetRef,
  hasCureSheetRef,
  standingNeedsAttention,
} from '../models/cure-sheet-ref';
import { closeSubscription, runTeardown } from '../services/safe-teardown';

@Component({
  selector: 'app-project-information',
  templateUrl: './project-information.component.html',
  styleUrls: ['./project-information.component.scss'],
})
export class ProjectInformationComponent implements OnInit, OnDestroy {
  projectName: string = '';
  firstMeasurement: Date = new Date(0, 0, 0);
  lastMeasurement: Date = new Date(0, 0, 0);
  tempDataSubscription?: Subscription;
  selectedProjectSubscription?: Subscription;
  tempData: ProjectTemperatureData = {} as ProjectTemperatureData;
  currentTab: number = 1;
  /** Configured heat-input stages, in the viewer's units (converted server-side). */
  heatInputs: HeatInputStageResolved[] = [];

  /** Set only when the project carries one — absent is normal, see below. */
  cureSheetRef: CureSheetRef | null = null;
  cureSheetResolved: CureSheetProvenance | null = null;
  cureSheetLine = '';
  cureSheetNeedsAttention = false;
  cureSheetPdfError = '';

  constructor(
    public projectService: ProjectService,
    private cureSheets: CureSheetService
  ) {}

  /**
   * Resolve the project's cure-sheet provenance, if it has any.
   *
   * ABSENT IS NORMAL: the ref is operator-chosen and optional, so most projects
   * have none and this renders nothing at all — no row, no "none". An empty row
   * would read as "no sheet was followed", a claim about the cure this data
   * cannot support.
   */
  private loadCureSheetProvenance(projectName: string): void {
    const project = this.projectService.projectList.find(
      (p) => p.ProjectName === projectName
    ) as { cureSheetRef?: CureSheetRef } | undefined;

    if (!hasCureSheetRef(project)) {
      this.cureSheetRef = null;
      this.cureSheetLine = '';
      return;
    }
    const ref = project!.cureSheetRef as CureSheetRef;
    this.cureSheetRef = ref;

    // Shown IMMEDIATELY, before resolution, saying the standing is unknown.
    // Waiting silently would leave a withdrawn document looking unremarkable
    // for as long as the lookup takes.
    this.cureSheetLine = describeCureSheetRef(ref, null);
    this.cureSheetNeedsAttention = false;

    this.cureSheets.getProvenance(projectName, ref.sheetId, ref.version).subscribe({
      next: (resolved) => {
        this.cureSheetResolved = resolved;
        this.cureSheetLine = describeCureSheetRef(ref, resolved);
        this.cureSheetNeedsAttention = standingNeedsAttention(resolved?.standing);
      },
      error: (err) => {
        // Stay with the unresolved line, which already says the standing is
        // unavailable. Do NOT fall back to a bare "Followed: X" — that would
        // let a withdrawn document read as current guidance, which is the exact
        // thing this feature exists to prevent.
        console.warn('Cure sheet provenance lookup failed', err?.status ?? err);
      },
    });
  }

  /**
   * Open the referenced PDF.
   *
   * Through the PROVENANCE route, not the audience one: the audience list is by
   * construction the set that excludes withdrawn versions, so it would fail to
   * serve exactly the document someone reviewing an incident needs.
   */
  openCureSheetPdf(): void {
    if (!this.cureSheetRef || !this.projectName) return;
    this.cureSheetPdfError = '';
    this.cureSheets
      .getProvenancePdf(this.projectName, this.cureSheetRef.sheetId, this.cureSheetRef.version)
      .subscribe({
        next: (pdf) => this.cureSheets.openPdf(pdf),
        error: (err) => {
          this.cureSheetPdfError =
            'Could not open the cure sheet. ' +
            (err?.status === 404
              ? 'It may no longer be available to your account.'
              : 'Please try again.');
        },
      });
  }

  ngOnInit(): void {
    this.projectService.isHelpOverlayVisible = false;

    console.log('Metric:' + this.projectService.isMetricUnit);

    this.selectedProjectSubscription =
      this.projectService.selectedProject.subscribe(
        (data) => {
          this.projectName = data;
          this.loadCureSheetProvenance(data);
        },
        (error) => console.log(error)
      );

    this.tempDataSubscription = this.projectService.temperatureData.subscribe(
      (data) => {
        if (data == null) {
          console.log('Error in Temp Comp: Temp data is null.');
          return;
        } else if (data.TRC == null) {
          console.log('Error in Temp Comp: Temp data > TRC is null.');
          return;
        }

        this.tempData = data;
        this.heatInputs = heatInputStages(data);
        if (this.tempData) {
          if (this.tempData.TRC.length > 0) {
            this.firstMeasurement = this.tempData.TRC[0].T;
            this.lastMeasurement =
              this.tempData.TRC[this.tempData.TRC.length - 1].T;
          }
        }
        //this.tempData.MI
      },
      (error) => console.log(error)
    );
  }

  /**
   * The selected project's list row.
   *
   * Version, fibre and user parameters are NOT on the telemetry payload in any
   * usable form: its U is always sent empty and it carries no version at all.
   * The list row carries them, already decoded, so the pane reads them there.
   */
  private projectRow(): ProjectInfo | undefined {
    if (!this.projectName) return undefined;
    return this.projectService.projectList.find((p) => p.ProjectName === this.projectName);
  }

  /**
   * Fibre type.
   *
   * The list row's FiberType first: the server reads it from the calibration name
   * and strips the desktop's mojibake. A live document has no FIB key at all, so
   * reading FIB alone left the cell blank on every live cure. FIB is kept as the
   * fallback and it is BASE64, so it is decoded.
   */
  fiberLabel(): string {
    const fromRow = this.projectRow()?.FiberType?.trim();
    if (fromRow) {
      return fromRow;
    }
    const raw = this.tempData?.FIB;
    if (!raw) {
      return '—';
    }
    try {
      const decoded = atob(raw).replace(/\0+$/, '').trim();
      return decoded.length > 0 ? decoded : raw;
    } catch {
      // Not base64 after all — show what we were given rather than nothing.
      return raw;
    }
  }

  /**
   * Liner software version (MeasuredLinerVersion), or an em dash.
   *
   * The cell was hardcoded empty. An earlier helper read VS, which is the
   * payload's VALIDATION STATUS, not a version, and was never wired up.
   */
  versionLabel(): string {
    const version = this.projectRow()?.Version?.trim();
    return version ? version : '—';
  }

  /**
   * Sampling interval without the float32 tail.
   *
   * SI travels as a single-precision float, so 0.15 arrives as 0.150000006 and
   * was printed that way. Six significant digits is far past the instrument's
   * resolution and well short of the float noise; it is locale-free, and it
   * never rounds a real value such as 0.125 or an imperial 0.4921 away.
   */
  samplingIntervalLabel(): string {
    const si = this.tempData?.SI;
    return typeof si === 'number' && Number.isFinite(si) ? String(Number(si.toPrecision(6))) : '—';
  }

  /** The operator's job fields, from the list row. Empty names and values are dropped. */
  userParameters(): UserParameter[] {
    return (this.projectRow()?.UserParameter ?? []).filter(
      (p) => (p?.Name ?? '').trim() !== '' || (p?.Value ?? '').trim() !== ''
    );
  }

  /** Teardown must never abort a route deactivation — see safe-teardown.ts. */
  ngOnDestroy(): void {
    runTeardown('ProjectInformationComponent.ngOnDestroy', () => {
      closeSubscription('selectedProjectSubscription', this.selectedProjectSubscription);
      closeSubscription('tempDataSubscription', this.tempDataSubscription);
    });
  }

  selectTab(tabNo: number) {
    this.currentTab = tabNo;
  }
}
